import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Loader from "Components/UI/Loader";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import { convertVolume, convertUnit, extractNumber } from "Library/Utils";
import MaterialSelect from "@mui/material/Select";
// Images
import images from "Assets/images";
// css
import "Components/Common/Modal/style.css";
import "Components/Core/BookingFormModule/new-booking-form.css";
import { useApiCall } from "Hooks/useApiCall";
import { MenuItem } from "@mui/material";

import { EquipmentType } from "Network/Core/EquipmentMaster/types";

import React from "react";
import {
  createEquipmentUsage,
  updateEquipmentUsage,
} from "Network/Core/EquipmentMaster/equipmentUsage";
import { getListOfEquipmentRegistry } from "Network/Core/EquipmentMaster/equipmentRegistry";
import FileUploadModal from "../FileuploaderModal/FileUploadModal";
import FileUpload from "Components/UI/FileUpload";
import { useTranslationCheck } from "Hooks/useTranslationCheck";

export interface MonthlyUsageModelProps {
  handleClose?: any;
  existingData: EquipmentType;
}

const MonthlyUsageModel = ({
  handleClose,
  existingData,
}: MonthlyUsageModelProps) => {
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const site = getLocalStorage(ls_vars.PROJECT_SITE_ID);
  const { t, tCheck } = useTranslationCheck();
  const { call, recursivePaginatedCall } = useApiCall();
  const [isUploadFileVisible, setIsUploadFileVisible] = useState<boolean>(
    existingData?.id ? false : true
  );
  const [scheduledDate, setScheduledDate] = useState<Date | string>("");
  const [equipmentUsage, setEquipmentUsage] = useState<any>({
    registeredEquipment: 0,
    month: "",
    year: "",
    noOfHours: "0.00",
    litresOrGallons: "0.00",
    added_by: getLocalStorage(ls_vars.USER_DATA)?.id,
  });
  const countryUom = getLocalStorage(ls_vars.COUNTRY_UOM);

  const [equipmentTypesOptions, setEquipmentTypesOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [equipmentList, setEquipmentList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [monthOptions, setMonthOptions] = useState<
    { label: string; value: string }[]>([])
  const months = [
    { label: "January", value: "January" },
    { label: "February", value: "February" },
    { label: "March", value: "March" },
    { label: "April", value: "April" },
    { label: "May", value: "May" },
    { label: "June", value: "June" },
    { label: "July", value: "July" },
    { label: "August", value: "August" },
    { label: "September", value: "September" },
    { label: "October", value: "October" },
    { label: "November", value: "November" },
    { label: "December", value: "December" },
  ];

  const [yearOptions, setYearOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [validationError, setValidationError] = useState("");

  // Helper functions for volume conversion
  const convertToLiters = (value: number): number => {
    if (countryUom?.volume?.name === 'Gallons') {
      return convertUnit.volume.gal.to.l(extractNumber(value?.toString()));
    }
    return value; // Already in liters
  };

  const convertFromLiters = (value: number): number => {
    if (countryUom?.volume?.name === 'Gallons') {
      return convertUnit.volume.l.to.gal(extractNumber(value?.toString()));
    }
    return value; // Keep in liters
  };

  const handleChange = (e: { target: { name: string; value: any } }) => {
    setIsUploadFileVisible(false);
    const { name, value } = e.target;

    if (name === "noOfHours" || name === "litresOrGallons") {
      const numValue = value === "" ? 0.0 : parseFloat(value);
      setEquipmentUsage((prev: any) => ({
        ...prev,
        [name]: numValue,
      }));
    } else if (name === "registeredEquipment") {
      const selectedEquipment = equipmentList.find((item) => item.id === value);
      const currentYear = new Date().getFullYear();
      const arrivalYear = new Date(selectedEquipment?.scheduledArrival).getFullYear();
      const existingYear = existingData?.year ? parseInt(existingData.year) : null;

      // Start from current year, arrival year, or existing year (whichever is latest) and go back 1 years
      const scheduledArrivalYear = Math.min(arrivalYear, existingYear || currentYear);
      const startYear = scheduledArrivalYear - 1;
      const endYear = currentYear;

      const years = [];
      for (let year = endYear; year >= startYear; year--) {
        years.push({ label: year.toString(), value: year.toString() });
      }

      setYearOptions(years);
      setScheduledDate(selectedEquipment?.scheduledArrival);
      setValidationError("");

      // Reset dependent fields when equipment changes (but preserve existing data if editing)
      setEquipmentUsage((prev: any) => ({
        ...prev,
        [name]: value,
        year: existingData?.id ? prev.year : "",
        month: existingData?.id ? prev.month : "",
      }));
    } else if (name === "year") {
      const scheduledMonth = new Date(scheduledDate).getMonth();
      const scheduledYear = new Date(scheduledDate).getFullYear();

      let filteredMonths;
      let shouldResetMonth = false;

      if (parseInt(value) === scheduledYear) {
        filteredMonths = months.slice(scheduledMonth);
        // Check if current month selection is still valid
        shouldResetMonth = equipmentUsage.month &&
          !filteredMonths.find(m => m.value === equipmentUsage.month);
      } else {
        filteredMonths = months;
      }

      setMonthOptions(months);

      // Update equipment usage with potential month reset
      setEquipmentUsage((prev: any) => ({
        ...prev,
        year: value,
        month: shouldResetMonth ? "" : prev.month,
      }));

    } else {
      setEquipmentUsage((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const resetState = (() => {
    setEquipmentUsage({
      registeredEquipment: 0,
      month: "",
      year: "",
      noOfHours: "0.00",
      litresOrGallons: "0.00",
      added_by: getLocalStorage(ls_vars.USER_DATA)?.id,
    });
    setScheduledDate("");
    setYearOptions([]);
    setMonthOptions([]);
    setValidationError("");
    setIsUploadFileVisible(existingData?.id ? false : true);
  });

  const fetchEquipmentTypes = async () => {
    call(
      () => getListOfEquipmentRegistry(region, project.ref, site),
      (res: any) => {
        const options = res?.results
          ?.map((item: any) => ({
            label: `[${item.scheduledArrival}]-${item.equipmentType}-${item.company}-${item.regMake}-${item.regModel}-${item.regYear}`,
            value: item.id,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label));
        setEquipmentTypesOptions(options);
        setEquipmentList(res?.results)
      }
    );
  };

  const handleCreateEquipmentUsageSubmit = async (e: any) => {
    e.preventDefault();
    if (parseFloat(equipmentUsage.noOfHours) <= 0 && parseFloat(equipmentUsage.litresOrGallons) <= 0) {
      const volumeUnit = countryUom?.volume?.name === 'Gallons' ? 'Gallons' : 'Litres';
      setValidationError(`Please enter either No. of Hours or ${volumeUnit}.`);
      return;
    }
    setValidationError("");

    // Convert volume to liters for database storage
    const volumeInLiters = equipmentUsage.litresOrGallons
      ? convertToLiters(extractNumber(equipmentUsage.litresOrGallons?.toString()))
      : 0;

    const data = {
      registeredEquipmentId: equipmentUsage.registeredEquipment,
      month: equipmentUsage.month,
      year: parseInt(equipmentUsage.year, 10), // Convert year to number
      noOfHours: equipmentUsage.noOfHours
        ? parseFloat(equipmentUsage.noOfHours).toFixed(2)
        : "0.00",
      litresOrGallons: volumeInLiters.toFixed(2),
      added_by: getLocalStorage(ls_vars.USER_DATA)?.id,
    };

    call(
      () => createEquipmentUsage(data, region, project.ref),
      (data) => {
        setIsLoading(false);
        resetState();
        handleClose();
      },
      { successMessage: "Translation.equipment_usage_add_success" }
    );
    setIsLoading(false);
  };

  const handleUpdateEquipmentUsageSubmit = async (e: any) => {
    e.preventDefault();
    if (!existingData || existingData.id === undefined) {
      console.error("Cannot update registeredEquipment: Invalid ID");
      return;
    }

    if (parseFloat(equipmentUsage.noOfHours) <= 0 && parseFloat(equipmentUsage.litresOrGallons) <= 0) {
      const volumeUnit = countryUom?.volume?.name === 'Gallons' ? 'Gallons' : 'Litres';
      setValidationError(`Please enter either No. of Hours or ${volumeUnit}.`);
      return;
    }
    setValidationError("");

    // Convert volume to liters for database storage
    const volumeInLiters = equipmentUsage.litresOrGallons
      ? convertToLiters(extractNumber(equipmentUsage.litresOrGallons?.toString()))
      : 0;

    const updatedData = {
      ...equipmentUsage,
      litresOrGallons: volumeInLiters.toFixed(2),
    };

    const id = existingData.id;
    call(
      () => updateEquipmentUsage(id, updatedData, region, project.ref),
      (data) => {
        setIsLoading(false);
        resetState();
        handleClose();
      },
      { successMessage: "Translation.equipment_usage_edit_success" }
    );
    setIsLoading(false);
  };

  useEffect(() => {
    fetchEquipmentTypes();
  }, []);

  useEffect(() => {
    if (existingData && existingData.id && equipmentList.length > 0) {
      // Find the equipment to get scheduled date
      const selectedEquipment = equipmentList.find((item) => item.id === existingData.registeredEquipment);

      if (selectedEquipment) {
        const currentYear = new Date().getFullYear();
        const arrivalYear = new Date(selectedEquipment?.scheduledArrival).getFullYear();
        const existingYear = existingData?.year ? parseInt(existingData.year) : null;

        // Start from current year, arrival year, or existing year (whichever is latest) and go back 1 years
        const scheduledArrivalYear = Math.min(arrivalYear, existingYear ?? 0);
        const startYear = scheduledArrivalYear - 1;
        const endYear = currentYear;

        const years = [];
        for (let year = endYear; year >= startYear; year--) {
          years.push({ label: year.toString(), value: year.toString() });
        }
        setYearOptions(years);

        // Set up month options based on scheduled arrival
        const scheduledMonth = new Date(selectedEquipment.scheduledArrival).getMonth();
        const scheduledYear = new Date(selectedEquipment.scheduledArrival).getFullYear();

        if (existingYear === scheduledYear) {
          setMonthOptions(months.slice(scheduledMonth));
        } else {
          setMonthOptions(months);
        }

        setScheduledDate(selectedEquipment?.scheduledArrival);
      } else {
        // If equipment not found, still set up basic options
        const existingYear = parseInt(existingData.year);
        const endYear = existingYear - 3;

        const years = [];
        for (let year = existingYear; year >= endYear; year--) {
          years.push({ label: year.toString(), value: year.toString() });
        }
        setYearOptions(years);
        setMonthOptions(months);
      }

      // Convert volume from liters (stored in DB) to display unit
      const displayVolume = existingData.litresOrGallons
        ? convertFromLiters(extractNumber(existingData.litresOrGallons?.toString()))
        : 0;

      setEquipmentUsage({
        registeredEquipment: existingData.registeredEquipment,
        month: existingData.month,
        year: existingData.year,
        noOfHours: existingData.noOfHours || "0.00",
        litresOrGallons: displayVolume.toFixed(2),
        added_by: getLocalStorage(ls_vars.USER_DATA)?.id,
      });
    } else {
      resetState();
      const currentYear = new Date().getFullYear();
      const startYear = currentYear - 1;
      const endYear = currentYear;

      const years = [];
      for (let year = endYear; year >= startYear; year--) {
        years.push({ label: year.toString(), value: year.toString() });
      }
      setYearOptions(years);
      setMonthOptions(months);
    }
  }, [existingData, equipmentList]);

  return (
    <>
      {isLoading && <Loader />}
      <div
        className="modal fade  show vc-user-invite"
        id="invite_user_Modal"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable ">
          <div className="modal-content">
            <div className="modal-header">
              <div className="invite-user-header">
                <h2 className="text-left">
                  {t(
                    existingData && existingData.id
                      ? "Translation.equipment_usage_edit"
                      : "Translation.add_equipment_monthly_usage_capture"
                  )}
                </h2>
              </div>
              <div className="invite-user-header vc-invite-from-procore-link">
                <Link onClick={handleClose} to="">
                  {t("Translation.close")}
                  <img src={images.modalCloseIcon} alt="" />
                </Link>
              </div>
            </div>
            <div className="modal-body">
              <div className="custom-form ">
                <form
                  onSubmit={
                    existingData && existingData.id
                      ? handleUpdateEquipmentUsageSubmit
                      : handleCreateEquipmentUsageSubmit
                  }
                >
                  <div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <label
                          htmlFor="equipmentName"
                          className="form-label mb-0"
                        >
                          <span>{`${t("Translation.equipment_name")}`}</span>
                        </label>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{
                            width: "22rem",
                          }}
                          inputProps={{
                            className: "form-control",
                            id: "registeredEquipment",
                            name: "registeredEquipment",
                            required: true,
                          }}
                          value={equipmentUsage.registeredEquipment || ""} // Add explicit value prop here,
                          onChange={(e: any) => handleChange(e)}
                        >
                          {equipmentTypesOptions.map((item) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <label htmlFor="fuelType" className="form-label mb-0">
                          {t("Translation.year")}
                        </label>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{
                            width: "22rem",
                          }}
                          inputProps={{
                            className: "form-control",
                            id: "year",
                            name: "year",
                            required: true,
                          }}
                          value={equipmentUsage.year || ""} // Add explicit value prop here,
                          onChange={(e: any) => handleChange(e)}
                        >
                          {yearOptions.map((item) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <label htmlFor="month" className="form-label mb-0">
                          {t("Translation.month")}
                        </label>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{
                            width: "22rem",
                          }}
                          inputProps={{
                            className: "form-control",
                            id: "month",
                            name: "month",
                            required: true,
                          }}
                          value={equipmentUsage.month || ""} // Add explicit value prop here
                          onChange={(e: any) => handleChange(e)}
                        >
                          {monthOptions.map((item) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <label htmlFor="noOfHours" className="form-label mb-0">
                          {t("Translation.no_of_hours")}
                        </label>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="number"
                          step="0.01" // This allows for 2 decimal places
                          className="form-control"
                          id="noOfHours"
                          name="noOfHours"
                          value={equipmentUsage.noOfHours}
                          onChange={(e: any) => handleChange(e)}
                          onClick={(e: any) => {
                            if (parseFloat(e.target.value) === 0) {
                              e.target.select();
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <label htmlFor="liters" className="form-label mb-0">
                          {t(countryUom?.volume?.name === 'Gallons' ? "Translation.gallons" : "Translation.litres")}
                        </label>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="number"
                          step="0.01" // This allows for 2 decimal places
                          className="form-control"
                          id="litresOrGallons"
                          name="litresOrGallons"
                          value={equipmentUsage.litresOrGallons}
                          onChange={(e: any) => handleChange(e)}
                          onClick={(e: any) => {
                            if (parseFloat(e.target.value) === 0) {
                              e.target.select();
                            }
                          }}
                        />
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-12 mt-3 d-flex justify-content-end">
                        <button type="submit" className="btn theme-btn">
                          {t("Translation.submit")}
                        </button>
                      </div>
                    </div>
                    <div className="modal-footer">
                      {validationError && (
                        <p className="text-danger">{validationError}</p>
                      )}
                    </div>
                  </div>
                </form>
                <div>
                  {isUploadFileVisible && (
                    <FileUpload
                      project={project.ref}
                      region={region}
                      type="equipmentUsage"
                      handleClose={() => {
                        setIsUploadFileVisible(false);
                      }}
                      onUploadSuccess={() => {
                        setIsUploadFileVisible(false);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonthlyUsageModel;
